import { Data, Model, Utils } from '@singularsystems/neo-core';
import { AxiosPromise } from 'axios';
import { injectable } from 'inversify';
import { AppService, Types } from '../DomainTypes';
import { IInvestmentHostApiClient } from './IInvestmentHostApiClient';
import InvestorInvestmentPagedLookupCriteria from '../Models/InvestorInvestments/Lookups/InvestorInvestmentPagedLookupCriteria';
import InvestorInvestmentPagedLookup from '../Models/InvestorInvestments/Lookups/InvestorInvestmentPagedLookup';
import InvestorInvestment from '../Models/InvestorInvestments/InvestorInvestment';
import InvestorInvestmentLookup from '../Models/InvestorInvestments/Lookups/InvestorInvestmentLookup';
import ShareTransactionCommand from '../Models/Transactions/Commands/ShareTransactionCommand';
import CreateTransactionResult from '../Models/Transactions/Lookups/CreateTransactionResult';
import DeleteTransactionResult from '../Models/Transactions/Lookups/DeleteTransactionResult';
import { IInvestmentHostSaveResult } from '../Models/Investment/Lookups/InvestmentSaveResult';
import DeleteTransactionCommand from '../Models/Transactions/Commands/DeleteTransactionCommand';

export interface IInvestorInvestmentApiClient extends IInvestmentHostApiClient {

    /**
     * Gets a list of investor investments for a paged grid.
     */
    getInvestorInvestmentsPaged(pageRequest: Model.PartialPlainNonTrackedObject<Data.PageRequest<InvestorInvestmentPagedLookupCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<InvestorInvestmentPagedLookup>>>;

    /**
     * Gets an investor investment for a given id.
     */
    getInvestorInvestment(investorInvestmentId: number): AxiosPromise<Model.PlainTrackedObject<InvestorInvestment>>;

    /**
     * Gets a list of investor investments that match the search text. Limited to 50 results.
     */
    getInvestorInvestmentLookups(searchText: string, hasVestingTrust: boolean | null, issuerInvestmentId: number | null): AxiosPromise<Array<Model.PlainObject<InvestorInvestmentLookup>>>;

    /** 
     * Saves an investor investment.
     */
    saveInvestorInvestment(investorInvestment: Model.PartialPlainObject<InvestorInvestment>): AxiosPromise<IInvestmentHostSaveResult<InvestorInvestment>>;

    /**
     * Creates a share transaction from the details in the command.
     */
    createShareTransaction(command: Model.PartialPlainObject<ShareTransactionCommand>): AxiosPromise<Model.PlainObject<CreateTransactionResult>>;

    deleteShareTransaction(command: Model.PartialPlainNonTrackedObject<DeleteTransactionCommand>): AxiosPromise;

    // Client only properties / methods
}

@injectable()
export default class InvestorInvestmentApiClient extends Data.ApiClientBase implements IInvestorInvestmentApiClient {

    constructor (config = AppService.get(Types.App.Config)) {
        super(`${config.apiPath}/investor-investments`);
    }

    public getInvestorInvestmentsPaged(pageRequest: Model.PartialPlainNonTrackedObject<Data.PageRequest<InvestorInvestmentPagedLookupCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<InvestorInvestmentPagedLookup>>> {
        return this.axios.get(`${this.apiPath}/paged?${Utils.getQueryString(pageRequest)}`);
    }

    public getInvestorInvestment(investorInvestmentId: number): AxiosPromise<Model.PlainTrackedObject<InvestorInvestment>> {
        return this.axios.get(`${this.apiPath}/${investorInvestmentId}`);
    }

    public getInvestorInvestmentLookups(searchText: string, hasVestingTrust: boolean | null, issuerInvestmentId: number | null): AxiosPromise<Array<Model.PlainObject<InvestorInvestmentLookup>>> {
        var url = `${this.apiPath}/lookups?searchText=${encodeURIComponent(searchText)}`
        if (hasVestingTrust !== null) {
            url += `&hasVestingTrust=${hasVestingTrust}`
        }
        if (issuerInvestmentId) {
            url += `&issuerInvestmentId=${issuerInvestmentId}`
        }
        return this.axios.get(url);
    }

    public saveInvestorInvestment(investorInvestment: Model.PartialPlainObject<InvestorInvestment>): AxiosPromise<IInvestmentHostSaveResult<InvestorInvestment>> {
        return this.axios.post(`${this.apiPath}`, investorInvestment);
    }

    public createShareTransaction(command: Model.PartialPlainObject<ShareTransactionCommand>): AxiosPromise<Model.PlainObject<CreateTransactionResult>> {
        return this.axios.post(`${this.apiPath}/transaction`, command);
    }

    public deleteShareTransaction(command: Model.PartialPlainNonTrackedObject<DeleteTransactionCommand>): AxiosPromise<Model.PlainObject<DeleteTransactionResult>> {
        return this.axios.delete(`${this.apiPath}/transaction?${Utils.getQueryString(command)}`);
    }

    // Client only properties / methods
}